/**************************************** PROJECT MODULES *****************************************/
import {SupportedLang, supportedLangs, config} from '@canimmunize/core-imports';
import {BaseStore} from '../base-store';
import {isDevelopment} from 'env-var-helpers';

/************************************** THIRD-PARTY MODULES ***************************************/
import {observable, action} from 'mobx';

import {
    getUrlPathBeforeLang,
    getUrlPathAfterLang,
    getLangFromUrlPathname,
} from 'mad-utils/lib/react';

/********************************************* EXPORT *********************************************/
/**
 * Store current language
 * Singleton
 * @export LanguageStore
 */
export class LanguageStore extends BaseStore {
    public static Instance: LanguageStore | null = null;

    @observable
    public language: SupportedLang;

    /**
     * Fully redirects page after changing language
     */
    @action
    public setLanguage = (lang: SupportedLang) => {
        this.setData('language', lang);
        const getUrlBefore = getUrlPathBeforeLang(window.location.href);
        const getUrlAfter = getUrlPathAfterLang(window.location.href);
        const newFullUrl = `${getUrlBefore}/${lang}/${getUrlAfter}`;
        (window as any).location = newFullUrl;
    };

    /**
     * LanguageStore singleton factory
     * Gets class props from sessionStorage; assigns default for each not found
     */
    public static new = (): LanguageStore => {
        if (!LanguageStore.Instance) LanguageStore.Instance = new LanguageStore();
        return LanguageStore.Instance;
    };

    /**
     * Build LanguageStore object
     * Gets class props from sessionStorage
     * Assigns default for each not found
     *
     * @return {RootStore} The data store itself, including its children,
     *                     pulled in by composition
     */
    private constructor() {
        super();
        this._initializeLanguage();
    }

    /**
     * Set the active language based on the query param
     */
    @action
    private _initializeLanguage() {
        const defaultLang = config.i18n.defaultLang || `en`;
        const curLang =
            (getLangFromUrlPathname(
                window.location.href,
                supportedLangs,
                defaultLang
            ) as SupportedLang) ||
            sessionStorage.getItem(`language`) ||
            defaultLang;
        if (supportedLangs.some(curSupportedLang => curLang === curSupportedLang)) {
            this.language = curLang as SupportedLang;
            sessionStorage.setItem(`language`, curLang);
        } else {
            this.language = defaultLang as SupportedLang;
            sessionStorage.setItem(`language`, defaultLang);
        }
    }
}
